import { useCallback, useMemo } from 'react';

import {
  type ReferenceBookLookups,
  buildReferenceBookLookup,
  getRelevantReferenceBookTypes,
  resolveReferenceBookValue,
  translateAuditKey,
  walkAndResolveFields,
} from '@gasbi/shared/web/Audit';
import { useTranslation } from 'react-i18next';

import { useGetEntityTypeNamesQuery } from '@/features/audit/auditApi';
import { useGetConsumerCategoriesQuery } from '@/features/consumerCategories/consumerCategoriesApi';
import { useGetContactPersonPositionsQuery } from '@/features/contactPersonPositions/contactPersonPositionsApi';
import { useGetContractTypesQuery } from '@/features/contractTypes/contractTypesApi';
import { useGetIndustriesQuery } from '@/features/industries/industriesApi';
import { useGetOrganizationalLegalFormsQuery } from '@/features/organizationalLegalForms/organizationalLegalFormsApi';

export const useGetAvailableEntities = (
  auditEntryId: string,
  anonymized = false,
) => {
  const { t } = useTranslation();
  const { data: entityTypes = [] } = useGetEntityTypeNamesQuery(
    { auditEntryId, anonymized },
    {
      skip: !auditEntryId,
    },
  );

  return useMemo(
    () =>
      entityTypes.map(e => ({
        entityTypeName: e,
        entityDisplayName: t(`Audit.EntityTypeName.${e}`, e) || e,
      })),
    [entityTypes, t],
  );
};

export const useGetEntityTypeName = (value: string) => {
  const { t } = useTranslation();

  return t(`Audit.EntityTypeName.${value}`, value);
};

const getTranslationKeyForValue = (key: string, value: unknown) => {
  switch (key) {
    case 'contractcontactperson.role':
      return `ContactPersonRole.${value}`;
    case 'contractentity.pricetype':
      return `contractPriceType.${value}`;
    case 'contractentity.documentstatus':
      return `documentStatus.${value}`;
    case 'contractentity.contractstatus':
      return `contractStatus.${value}`;
    case 'counterpartyentity.passporttype':
      return `passportType.${value}`;
    case 'nominationentity.status':
      return `nominationStatus.${value}`;
    case 'renominationentity.status':
      return `renominationStatus.${value}`;
    case 'contractaddendumentity.types.array':
    case 'contractaddendumentity.changes.changetype':
      return `contractAddendumType.${value}`;
    case 'resourceentity.resourcetype':
      return `resourceType.${value}`;
    case 'resourceentity.purpose':
      return `resourcePurpose.${value}`;
    case 'resourceentity.storagepointtype':
      return `storagePointType.${value}`;
    case 'resourceentity.intakeschedulemode':
    case 'resourceentity.schedulemode':
    case 'resourceentity.distribution.schedulemode':
      return `resourceScheduleMode.${value}`;
    case 'resourceentity.movementtype':
      return `resourceOperationType.${value}`;
    case 'identityemployee.status':
      return `Enum.EmployeeStatus.${value}`;
    case 'identityemployee.emaildeliverystatus':
      return `Enum.EmailDeliveryStatus.${value}`;
    // Contract terms blocks (ContractTermsAuditSnapshots) — enums live under `values`.
    case 'contractadvancetermsentity.values.periodicity':
      return `AdvancePeriodicity.${value}`;
    case 'contractadvancetermsentity.values.formationreferencemonth':
      return `AdvanceFormationReferenceMonth.${value}`;
    case 'contractadvancetermsentity.values.calculationbase':
      return `AdvanceCalculationBase.${value}`;
    case 'contractadvancetermsentity.values.paymentdaycountkind':
    case 'contractpaymenttermsentity.values.advancedaycountkind':
    case 'contractpaymenttermsentity.values.deferreddaycountkind':
      return `DayCountKind.${value}`;
    case 'contractpaymenttermsentity.values.paymenttype':
      return `PaymentType.${value}`;
    case 'contractpaymenttermsentity.values.advancedueevent':
    case 'contractpaymenttermsentity.values.deferreddueevent':
      return `PaymentDueEvent.${value}`;
    case 'contractpenaltytermentity.values.rateunit':
      return `PenaltyRateUnit.${value}`;
    case 'contractpenaltytermentity.values.calculationbase':
    case 'contractfinetermentity.values.calculationbase':
      return `SanctionCalculationBase.${value}`;
    case 'contractpenaltytermentity.values.accrualstartevent':
      return `PenaltyAccrualStartEvent.${value}`;
    case 'contractfinetermentity.values.type':
      return `FineType.${value}`;
    case 'contractfinetermentity.values.amounttype':
      return `FineAmountType.${value}`;
    case 'contractfinetermentity.values.condition':
      return `FineCondition.${value}`;
    case 'contractimbalancetermsentity.values.calculationbase':
      return `ImbalanceCalculationBase.${value}`;
    case 'contractimbalancetermsentity.values.pricesource':
      return `ImbalancePriceSource.${value}`;
    case 'contractimbalancetermsentity.values.calculationperiod':
      return `ImbalanceCalculationPeriod.${value}`;
    default:
      return undefined;
  }
};

// Reference-book type names, matching Supply's backend ReferenceBookTypes.cs. Kept as one
// constant per type so the string only ever gets typed once, everywhere else references it.
const ReferenceBookType = {
  ConsumerCategory: 'ConsumerCategory',
  OrganizationalLegalForm: 'OrganizationalLegalForm',
  ContactPersonPosition: 'ContactPersonPosition',
  Industry: 'Industry',
  SupplyTariffType: 'SupplyTariffType',
} as const;

// auditKey (lowercase dotted path) -> reference-book type. Add a new line here whenever a
// GUID-shaped, reference-book-backed field shows up in an audit diff instead of its value.
const REFERENCE_BOOK_FIELDS: Record<string, string> = {
  'contractentity.consumercategoryid': ReferenceBookType.ConsumerCategory,
  'contractentity.industryid': ReferenceBookType.Industry,
  'contractentity.supplytarifftypeid': ReferenceBookType.SupplyTariffType,
  'counterpartyentity.organizationallegalformid':
    ReferenceBookType.OrganizationalLegalForm,
  'counterpartyentity.industryid': ReferenceBookType.Industry,
  'counterpartycontactperson.positionid':
    ReferenceBookType.ContactPersonPosition,
  'contractcontactperson.positionid': ReferenceBookType.ContactPersonPosition,
};

export const useWithTranslatedFields = (entityTypeName?: string) => {
  const { t } = useTranslation();

  // Only fetch the reference books this entity type's audit can actually reference — e.g. a
  // ConsumptionObjectEntity audit never has a ConsumerCategory/OrganizationalLegalForm/
  // ContactPersonPosition field, so none of these three fire for it.
  const relevantTypes = useMemo(
    () => getRelevantReferenceBookTypes(entityTypeName, REFERENCE_BOOK_FIELDS),
    [entityTypeName],
  );

  // Same reference-book hooks Supply's own forms already call — RTK Query dedupes/caches
  // against those, so this rarely triggers a new network request of its own.
  const { data: consumerCategories } = useGetConsumerCategoriesQuery(
    undefined,
    {
      skip: !relevantTypes.has(ReferenceBookType.ConsumerCategory),
    },
  );
  const { data: organizationalLegalForms } =
    useGetOrganizationalLegalFormsQuery(undefined, {
      skip: !relevantTypes.has(ReferenceBookType.OrganizationalLegalForm),
    });
  const { data: contactPersonPositions } = useGetContactPersonPositionsQuery(
    undefined,
    { skip: !relevantTypes.has(ReferenceBookType.ContactPersonPosition) },
  );
  const { data: industries } = useGetIndustriesQuery(undefined, {
    skip: !relevantTypes.has(ReferenceBookType.Industry),
  });
  // contractTypesApi is Supply Client's existing local name for the backend's
  // SupplyTariffType reference book (hits /supply-tariff-types) — reused as-is here.
  const { data: supplyTariffTypes } = useGetContractTypesQuery(undefined, {
    skip: !relevantTypes.has(ReferenceBookType.SupplyTariffType),
  });

  const referenceBookLookups: ReferenceBookLookups = useMemo(
    () => ({
      [ReferenceBookType.ConsumerCategory]:
        buildReferenceBookLookup(consumerCategories),
      [ReferenceBookType.OrganizationalLegalForm]: buildReferenceBookLookup(
        organizationalLegalForms,
      ),
      [ReferenceBookType.ContactPersonPosition]: buildReferenceBookLookup(
        contactPersonPositions,
      ),
      [ReferenceBookType.Industry]: buildReferenceBookLookup(industries),
      [ReferenceBookType.SupplyTariffType]:
        buildReferenceBookLookup(supplyTariffTypes),
    }),
    [
      consumerCategories,
      organizationalLegalForms,
      contactPersonPositions,
      industries,
      supplyTariffTypes,
    ],
  );

  return useCallback(
    (data: unknown) =>
      walkAndResolveFields(data, {
        translateKey: key => translateAuditKey(t, key),
        resolveValue: (key, value) => {
          const referenceBookLabel = resolveReferenceBookValue(
            key,
            value,
            REFERENCE_BOOK_FIELDS,
            referenceBookLookups,
          );
          if (referenceBookLabel !== undefined) return referenceBookLabel;

          const translationKeyForValue = getTranslationKeyForValue(key, value);
          return translationKeyForValue && value !== null
            ? t(translationKeyForValue, String(value))
            : value;
        },
      }),
    [t, referenceBookLookups],
  );
};
